import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Body1,
  Body1Strong,
  Button,
  Card,
  Caption1,
  Link,
  Subtitle1,
  Subtitle2,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Title2,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { BrainSparkleRegular, LightbulbRegular } from '@fluentui/react-icons';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';

import { FileIcon, FoldersNavDrawer, useItemTableStyles } from '@modules/items';
import { AccessType } from '@modules/access';
import { FULL_PATH_SUBMISSIONS_ITEM } from '@modules/submissions/constants';
import { TSingleSubmissionData } from '@modules/submissions/types/submissions.types';
import { SignInContext } from '@modules/users';
import { Path } from '@shared/components';
import { useFolderPathAsBreadcrumbs } from '@shared/hooks';
import { buildHref } from '@shared/utils';

import {
  COMPLIANCE_CHECK_DEVICE_CLASSIFICATION_LABELS,
  COMPLIANCE_CHECK_DEVICE_TYPE_LABELS,
  COMPLIANCE_CHECK_REGULATION_LABELS,
  getComplianceCheckDocument,
  MOCK_COMPLIANCE_CHECK_DOCUMENTS,
  MOCK_COMPLIANCE_CHECK_ISSUES,
  getComplianceCheckDocumentFindingCount,
  getComplianceCheckReportSummary,
  getTopComplianceCheckDocuments,
} from '../constants';
import { TComplianceCheckConfig, TComplianceCheckIssue } from '../types';
import ComplianceSuggestionDialog from './ComplianceSuggestionDialog';

const useStyles = makeStyles({
  pageContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXL,
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
  },
  headerMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    minWidth: 0,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  titleIcon: {
    fontSize: '24px',
    lineHeight: 1,
    flexShrink: 0,
  },
  headerBadges: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    marginTop: tokens.spacingVerticalS,
  },
  cardsRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalXXL,
    flexWrap: 'wrap',
    alignItems: 'stretch',
  },
  statsCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    width: '333px',
    maxWidth: '100%',
    flex: '1 0 333px',
    minHeight: '320px',
    '@media (max-width: 720px)': {
      flexBasis: '100%',
      width: '100%',
    },
  },
  statLabel: {
    color: tokens.colorNeutralForeground3,
  },
  summarySettings: {
    display: 'grid',
    gap: tokens.spacingVerticalM,
  },
  summarySetting: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    minWidth: 0,
  },
  summarySettingValue: {
    overflowWrap: 'anywhere',
  },
  summaryMetricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  summaryMetricCard: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: tokens.spacingVerticalS,
    minHeight: '80px',
    padding: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  summaryMetricValueDanger: {
    color: tokens.colorStatusDangerForeground1,
  },
  chartContainer: {
    position: 'relative',
    width: '100%',
    height: '190px',
  },
  chartCenterLabel: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  chartCenterValueDanger: {
    color: tokens.colorStatusDangerForeground1,
  },
  chartCenterCaptionDanger: {
    color: tokens.colorStatusDangerForeground1,
  },
  chartLegend: {
    display: 'grid',
    gap: tokens.spacingVerticalS,
  },
  legendRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  legendLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  legendDot: {
    width: '10px',
    height: '10px',
    borderRadius: tokens.borderRadiusCircular,
    flexShrink: 0,
  },
  topDocumentList: {
    display: 'grid',
    gap: tokens.spacingVerticalM,
  },
  topDocumentRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    alignItems: 'start',
    gap: tokens.spacingHorizontalM,
    '@media (max-width: 480px)': {
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: tokens.spacingVerticalS,
    },
  },
  topDocumentCellLayout: {
    alignItems: 'flex-start',
    minWidth: 0,
    overflow: 'hidden',
    '& > div': {
      minWidth: 0,
      overflow: 'hidden',
    },
  },
  topDocumentName: {
    display: 'block',
    maxWidth: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  topDocumentScope: {
    overflowWrap: 'anywhere',
  },
  topDocumentBadge: {
    whiteSpace: 'nowrap',
    justifySelf: 'end',
    alignSelf: 'start',
    '@media (max-width: 480px)': {
      justifySelf: 'start',
    },
  },
  tableCard: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  reviewedFilesTable: {
    width: '100%',
    tableLayout: 'fixed',
    '& tr': {
      height: 'auto',
    },
  },
  topAlignedCell: {
    verticalAlign: 'top',
  },
  reviewedFilesBodyCell: {
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
  },
  fileCellLayout: {
    alignItems: 'flex-start',
  },
  reviewScopeText: {
    overflowWrap: 'anywhere',
  },
  compactCell: {
    whiteSpace: 'nowrap',
  },
  reviewedFileName: {
    display: 'block',
    overflowWrap: 'anywhere',
  },
  issueList: {
    display: 'grid',
    gap: tokens.spacingVerticalL,
  },
  issueCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    borderRadius: tokens.borderRadiusLarge,
  },
  issueHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
  },
  issueMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  issueHeaderBadges: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  issueNumber: {
    color: tokens.colorNeutralForeground3,
  },
  sectionLabel: {
    color: tokens.colorNeutralForeground3,
  },
  documentChipList: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  documentChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
  },
  documentChipText: {
    display: 'block',
    minWidth: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  issueActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
  },
  disabledStateCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    width: '100%',
    maxWidth: '560px',
  },
});

type TProps = {
  data: TSingleSubmissionData;
  config: TComplianceCheckConfig;
};

type TSummaryMetric = {
  label: string;
  value: number;
  isDanger?: boolean;
};

const COMPLIANCE_CHECK_ISSUE_DOCUMENT_LINK_CONFIG: Record<
  string,
  Partial<Record<string, { highlightText: string }>>
> = {
  'electrical-safety-standard-edition-mismatch': {
    'gspr-checklist-report': {
      highlightText: 'IEC/EN 60601-1:2005+A1:2012+A2:2020 (Ed 3.1)',
    },
    'electrical-safety-emc-test-report': {
      highlightText: '(Ed 3.0)',
    },
  },
};

const getComplianceCheckDocumentLinkKey = (name: string, itemType: 'DOCUMENT' | 'FILE') => `${itemType}:${name}`;

const getSubmissionItemHref = (itemId: string, itemType: 'DOCUMENT' | 'FILE') =>
  itemType === 'DOCUMENT' ? `/document/${itemId}` : `/file/${itemId}`;

const ComplianceCheckResultContent = ({ data, config }: TProps) => {
  const {
    folder,
    submissionData: { submission, resolvedItems },
  } = data;
  const { organization } = useContext(SignInContext);
  const tableClasses = useItemTableStyles();
  const classes = useStyles();
  const navigate = useNavigate();
  const [selectedIssue, setSelectedIssue] = useState<TComplianceCheckIssue | undefined>();

  const drawerPath = [
    { _id: folder._id, name: folder.name, accessRight: folder.accessRight },
    { ...FULL_PATH_SUBMISSIONS_ITEM, href: `/product/${folder._id}/submissions` },
    {
      _id: submission._id,
      name: submission.title,
      accessRight: AccessType.NONE,
      href: `/product/${folder._id}/submissions/${submission._id}`,
    },
  ];
  const breadcrumbPath = [
    ...drawerPath,
    {
      _id: 'compliance-check-result',
      name: 'Compliance check',
      accessRight: AccessType.NONE,
    },
  ];

  const summary = getComplianceCheckReportSummary();
  const topDocuments = getTopComplianceCheckDocuments();
  const breadcrumbItems = useFolderPathAsBreadcrumbs(breadcrumbPath);
  const reviewContextPath = [submission.title, 'Compliance check'];
  const submissionItemLinksByDocument = submission.selectedItems.reduce<Record<string, string | null>>(
    (acc, submissionItem) => {
      if (!submissionItem.active) return acc;

      const resolvedItem = resolvedItems[submissionItem.item]?.item;
      if (!resolvedItem) return acc;

      const linkKey = getComplianceCheckDocumentLinkKey(resolvedItem.name, submissionItem.itemType);
      const nextHref = getSubmissionItemHref(submissionItem.item, submissionItem.itemType);

      if (!(linkKey in acc)) {
        acc[linkKey] = nextHref;
        return acc;
      }

      if (acc[linkKey] !== nextHref) acc[linkKey] = null;

      return acc;
    },
    {}
  );
  const getComplianceCheckDocumentHref = (documentId: string, issueId?: string) => {
    const document = getComplianceCheckDocument(documentId);
    if (!document) return undefined;

    const documentHref =
      submissionItemLinksByDocument[getComplianceCheckDocumentLinkKey(document.name, document.itemType)];
    if (!documentHref) return undefined;

    return buildHref(documentHref, {
      highlightText: issueId
        ? COMPLIANCE_CHECK_ISSUE_DOCUMENT_LINK_CONFIG[issueId]?.[document.id]?.highlightText
        : undefined,
    });
  };

  const chartData = [
    { name: 'Resolved', value: summary.resolvedIssues, color: tokens.colorBrandForeground1 },
    { name: 'Unresolved', value: summary.unresolvedIssues, color: tokens.colorStatusDangerForeground1 },
  ];
  const chartPaddingAngle = chartData.filter((entry) => entry.value > 0).length > 1 ? 4 : 0;
  const highlightedChartValue =
    summary.unresolvedIssues > 0
      ? {
          value: summary.unresolvedIssues,
          label: 'unresolved',
          valueClassName: classes.chartCenterValueDanger,
          labelClassName: classes.chartCenterCaptionDanger,
        }
      : {
          value: summary.resolvedIssues,
          label: 'resolved',
          valueClassName: undefined,
          labelClassName: undefined,
        };
  const summaryMetrics: TSummaryMetric[] = [
    { label: 'Documents analyzed', value: summary.totalDocuments },
    { label: 'Identified errors', value: summary.totalIssues },
  ];

  if (!organization.mock.isAiEnabled) {
    return (
      <div className="flex flex-1 h-full">
        <FoldersNavDrawer fullPath={drawerPath} selectedValue="submissions" folder={folder} />

        <div className="grow h-full w-full relative">
          <div className="absolute top-0 left-0 w-full h-full overflow-y-auto">
            <div className="flex flex-col pt-4 pb-16 pl-5 pr-8">
              <Path items={breadcrumbItems} hasCurrent size="large" />

              <Card className={classes.disabledStateCard} size="large">
                <Subtitle2>Compliance check</Subtitle2>
                <Body1>This compliance-check flow is disabled for the current organization.</Body1>
                <div className="flex">
                  <Button
                    appearance="primary"
                    onClick={() => navigate(`/product/${folder._id}/submissions/${submission._id}`)}
                  >
                    Back to submission
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 h-full">
      <FoldersNavDrawer fullPath={drawerPath} selectedValue="submissions" folder={folder} />

      <div className="grow h-full w-full relative">
        <div className="absolute top-0 left-0 w-full h-full overflow-y-auto">
          <div className="flex flex-col pt-4 pb-16 pl-5 pr-8">
            <Path items={breadcrumbItems} hasCurrent size="large" />

            <div className={mergeClasses(classes.pageContent, 'mt-4')}>
              <div className={classes.headerRow}>
                <div className={classes.headerMeta}>
                  <div className={classes.titleRow}>
                    <BrainSparkleRegular className={classes.titleIcon} />
                    <Subtitle1>Compliance check</Subtitle1>
                  </div>
                  <div className={classes.headerBadges}>
                    <Badge appearance="tint" color="informative">
                      {COMPLIANCE_CHECK_REGULATION_LABELS[config.regulation]}
                    </Badge>
                    <Badge appearance="tint" color="warning">
                      {COMPLIANCE_CHECK_DEVICE_CLASSIFICATION_LABELS[config.classification]}
                    </Badge>
                    <Badge appearance="tint" color="success">
                      {COMPLIANCE_CHECK_DEVICE_TYPE_LABELS[config.deviceType]}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className={classes.cardsRow}>
                <Card className={classes.statsCard} size="large">
                  <Subtitle2>Review summary</Subtitle2>

                  <div className={classes.summarySettings}>
                    <div className={classes.summarySetting}>
                      <Caption1 className={classes.statLabel}>Regulation / Standard</Caption1>
                      <Body1Strong className={classes.summarySettingValue}>
                        {COMPLIANCE_CHECK_REGULATION_LABELS[config.regulation]}
                      </Body1Strong>
                    </div>
                    <div className={classes.summarySetting}>
                      <Caption1 className={classes.statLabel}>Device Classification</Caption1>
                      <Body1Strong className={classes.summarySettingValue}>
                        {COMPLIANCE_CHECK_DEVICE_CLASSIFICATION_LABELS[config.classification]}
                      </Body1Strong>
                    </div>
                    <div className={classes.summarySetting}>
                      <Caption1 className={classes.statLabel}>Device Type</Caption1>
                      <Body1Strong className={classes.summarySettingValue}>
                        {COMPLIANCE_CHECK_DEVICE_TYPE_LABELS[config.deviceType]}
                      </Body1Strong>
                    </div>
                  </div>

                  <div className={classes.summaryMetricsGrid}>
                    {summaryMetrics.map((metric) => (
                      <div key={metric.label} className={classes.summaryMetricCard}>
                        <Caption1 className={classes.statLabel}>{metric.label}</Caption1>
                        <Title2 className={metric.isDanger ? classes.summaryMetricValueDanger : undefined}>
                          {metric.value}
                        </Title2>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className={classes.statsCard} size="large">
                  <Subtitle2>Resolved vs unresolved</Subtitle2>

                  <div className={classes.chartContainer}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chartData}
                          dataKey="value"
                          innerRadius="62%"
                          outerRadius="84%"
                          paddingAngle={chartPaddingAngle}
                        >
                          {chartData.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>

                    <div className={classes.chartCenterLabel}>
                      <div className="flex flex-col items-center">
                        <Title2 className={highlightedChartValue.valueClassName}>{highlightedChartValue.value}</Title2>
                        <Caption1 className={highlightedChartValue.labelClassName}>
                          {highlightedChartValue.label}
                        </Caption1>
                      </div>
                    </div>
                  </div>

                  <div className={classes.chartLegend}>
                    {chartData.map((entry) => (
                      <div key={entry.name} className={classes.legendRow}>
                        <div className={classes.legendLabel}>
                          <span className={classes.legendDot} style={{ backgroundColor: entry.color }} />
                          <Body1>{entry.name}</Body1>
                        </div>
                        <Body1Strong>{entry.value}</Body1Strong>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className={classes.statsCard} size="large">
                  <Subtitle2>Top files with errors</Subtitle2>

                  <div className={classes.topDocumentList}>
                    {topDocuments.map(({ document, findings }) => {
                      const documentLinkHref = getComplianceCheckDocumentHref(document.id);

                      return (
                        <div key={document.id} className={classes.topDocumentRow}>
                          <TableCellLayout
                            className={classes.topDocumentCellLayout}
                            media={
                              <FileIcon
                                type={document.itemType}
                                path={reviewContextPath}
                                isBlock={false}
                                isFrozen={false}
                                isTemplate={false}
                                mimetype={document.mimetype}
                                name={document.name}
                              />
                            }
                            description={
                              <Caption1 className={mergeClasses(classes.statLabel, classes.topDocumentScope)}>
                                {document.reviewScope}
                              </Caption1>
                            }
                          >
                            {documentLinkHref ? (
                              <Link
                                href={documentLinkHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={document.name}
                                className={classes.topDocumentName}
                              >
                                <Body1Strong>{document.name}</Body1Strong>
                              </Link>
                            ) : (
                              <Body1Strong className={classes.topDocumentName} title={document.name}>
                                {document.name}
                              </Body1Strong>
                            )}
                          </TableCellLayout>

                          <Badge appearance="tint" color="danger" className={classes.topDocumentBadge}>
                            {findings} finding{findings === 1 ? '' : 's'}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              </div>

              <div className="flex flex-col gap-4">
                <div>
                  <Subtitle1>Identified issues</Subtitle1>
                </div>

                <div className={classes.issueList}>
                  {MOCK_COMPLIANCE_CHECK_ISSUES.map((issue, index) => (
                    <Card key={issue.id} className={classes.issueCard} size="large">
                      <div className={classes.issueHeader}>
                        <div className={classes.issueMeta}>
                          <Caption1 className={classes.issueNumber}>Issue {index + 1}</Caption1>
                          <Subtitle2>{issue.title}</Subtitle2>
                        </div>

                        <div className={classes.issueHeaderBadges}>
                          <Badge appearance="tint" color="warning">
                            Major
                          </Badge>
                          <Badge appearance="tint" color="danger">
                            Unresolved
                          </Badge>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        <Caption1 className={classes.sectionLabel}>Affected documents</Caption1>
                        <div className={classes.documentChipList}>
                          {issue.documentIds.map((documentId) => {
                            const document = MOCK_COMPLIANCE_CHECK_DOCUMENTS.find((entry) => entry.id === documentId);
                            if (!document) return null;

                            const documentLinkHref = getComplianceCheckDocumentHref(document.id, issue.id);

                            return (
                              <span key={document.id} className={classes.documentChip}>
                                <FileIcon
                                  type={document.itemType}
                                  path={reviewContextPath}
                                  isBlock={false}
                                  isFrozen={false}
                                  isTemplate={false}
                                  mimetype={document.mimetype}
                                  name={document.name}
                                  size="small"
                                />
                                {documentLinkHref ? (
                                  <Link
                                    href={documentLinkHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={document.name}
                                    className={classes.documentChipText}
                                  >
                                    {document.name}
                                  </Link>
                                ) : (
                                  <Body1 className={classes.documentChipText} title={document.name}>
                                    {document.name}
                                  </Body1>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        <Caption1 className={classes.sectionLabel}>Error</Caption1>
                        <Body1>{issue.error}</Body1>
                      </div>

                      <div className={classes.issueActions}>
                        <Button
                          appearance="secondary"
                          icon={<LightbulbRegular />}
                          onClick={() => setSelectedIssue(issue)}
                        >
                          View suggestion
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                  <div>
                    <Subtitle1>Reviewed files</Subtitle1>
                  </div>
                </div>

                <Card className={mergeClasses(tableClasses.card, classes.tableCard)} size="large">
                  <Table className={mergeClasses(tableClasses.table, classes.reviewedFilesTable)}>
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell className={classes.topAlignedCell} style={{ width: '360px' }}>
                          <Body1Strong>File</Body1Strong>
                        </TableHeaderCell>
                        <TableHeaderCell className={classes.topAlignedCell}>
                          <Body1Strong>Review scope</Body1Strong>
                        </TableHeaderCell>
                        <TableHeaderCell className={classes.topAlignedCell} style={{ width: '96px' }}>
                          <Body1Strong>Findings</Body1Strong>
                        </TableHeaderCell>
                        <TableHeaderCell className={classes.topAlignedCell} style={{ width: '160px' }}>
                          <Body1Strong>Status</Body1Strong>
                        </TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody className={tableClasses.tableBody}>
                      {MOCK_COMPLIANCE_CHECK_DOCUMENTS.map((document) => {
                        const findings = getComplianceCheckDocumentFindingCount(document.id);
                        const documentLinkHref = getComplianceCheckDocumentHref(document.id);

                        return (
                          <TableRow key={document.id}>
                            <TableCell className={mergeClasses(classes.topAlignedCell, classes.reviewedFilesBodyCell)}>
                              <TableCellLayout
                                className={classes.fileCellLayout}
                                media={
                                  <FileIcon
                                    type={document.itemType}
                                    path={reviewContextPath}
                                    isBlock={false}
                                    isFrozen={false}
                                    isTemplate={false}
                                    mimetype={document.mimetype}
                                    name={document.name}
                                  />
                                }
                                description={
                                  <Caption1 className={classes.statLabel}>
                                    {document.itemType === 'FILE' ? 'Evidence file' : 'Controlled document'}
                                  </Caption1>
                                }
                              >
                                {documentLinkHref ? (
                                  <Link
                                    href={documentLinkHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={document.name}
                                    className={classes.reviewedFileName}
                                  >
                                    <Body1Strong>{document.name}</Body1Strong>
                                  </Link>
                                ) : (
                                  <Body1Strong className={classes.reviewedFileName} title={document.name}>
                                    {document.name}
                                  </Body1Strong>
                                )}
                              </TableCellLayout>
                            </TableCell>
                            <TableCell className={mergeClasses(classes.topAlignedCell, classes.reviewedFilesBodyCell)}>
                              <Body1 className={classes.reviewScopeText}>{document.reviewScope}</Body1>
                            </TableCell>
                            <TableCell
                              className={mergeClasses(
                                classes.topAlignedCell,
                                classes.reviewedFilesBodyCell,
                                classes.compactCell
                              )}
                            >
                              <Body1Strong>{findings}</Body1Strong>
                            </TableCell>
                            <TableCell
                              className={mergeClasses(
                                classes.topAlignedCell,
                                classes.reviewedFilesBodyCell,
                                classes.compactCell
                              )}
                            >
                              <Badge appearance="tint" color="success">
                                Analyzed
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ComplianceSuggestionDialog
        issue={selectedIssue}
        setIssue={setSelectedIssue}
        getDocumentLinkHref={getComplianceCheckDocumentHref}
      />
    </div>
  );
};

export default ComplianceCheckResultContent;
